import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { apiGet, apiPost, apiPatch } from '../../api/client';
import { AlertTriangle, Check, RefreshCw, Calendar, Loader2 } from 'lucide-react';

interface CalendarEntry {
  id: string;
  summary: string;
  primary?: boolean;
  accessRole?: string;
}

interface Props {
  type: 'studio' | 'personal';
  connected: boolean;
  selectedCalendarId?: string | null;
  onRefreshNeeded: () => void;
}

export function GoogleCalendarSelector({ type, connected, selectedCalendarId, onRefreshNeeded }: Props) {
  const [calendars, setCalendars] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null);

  const calendarsUrl = type === 'studio' ? '/calendar/calendars' : '/calendar/personal/calendars';
  const createUrl = type === 'studio' ? '/calendar/create-calendar' : '/calendar/personal/create-calendar';
  const selectUrl = type === 'studio' ? '/calendar/select' : '/calendar/personal/select';

  // Sync from parent prop
  const selectedId = selectedCalendarId ?? localSelectedId;

  const fetchCalendars = async () => {
    setLoading(true);
    setError(null);
    try {
      // apiFetch unwraps json.data, so res is already CalendarEntry[]
      const res = await apiGet<CalendarEntry[]>(calendarsUrl);
      setCalendars(Array.isArray(res) ? res : []);
    } catch {
      setError('Impossibile recuperare i calendari');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (connected) fetchCalendars();
  }, [connected]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      // apiPost unwraps json.data, so res is already { id, summary }
      const res = await apiPost<{ id: string; summary: string }>(createUrl);
      setLocalSelectedId(res.id);
      await fetchCalendars();
      onRefreshNeeded();
    } catch {
      setError('Impossibile creare il calendario. Riprova.');
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = async (calendarId: string) => {
    try {
      await apiPatch<{ success: boolean }>(selectUrl, { calendarId });
      setLocalSelectedId(calendarId);
      onRefreshNeeded();
    } catch {
      setError('Impossibile selezionare il calendario');
    }
  };

  if (!connected) return null;

  const currentSelectedId = selectedId;

  return (
    <div style={{
      padding: '0.75rem', background: 'var(--glass-bg-card)',
      borderRadius: 'var(--radius-card)', border: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', flexDirection: 'column', gap: '0.5rem',
    }}>
      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-text-muted)', margin: 0 }}>
        Calendario dedicato
      </p>

      {currentSelectedId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Check size={16} style={{ color: 'var(--color-success)' }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)', fontWeight: 500 }}>
            {calendars.find(c => c.id === currentSelectedId)?.summary ?? 'Calendario selezionato'}
          </span>
        </div>
      ) : (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning-text)', margin: 0 }}>
          <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Nessun calendario selezionato — usa &quot;primary&quot;
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <select
          value={currentSelectedId ?? ''}
          onChange={e => {
            const val = e.target.value;
            if (val === '__create__') handleCreate();
            else if (val) handleSelect(val);
          }}
          style={{
            flex: 1, minWidth: 0, padding: '0.375rem 0.5rem',
            fontSize: 'var(--text-xs)', borderRadius: 'var(--radius-interactive)',
            border: '1px solid rgba(255,255,255,0.10)', background: 'var(--glass-bg-card)',
            color: 'var(--color-text)',
          }}
          disabled={creating}
        >
          <option value="">— Seleziona calendario —</option>
          {calendars.map(c => (
            <option key={c.id} value={c.id}>
              {c.summary}{c.primary ? ' (principale)' : ''}
            </option>
          ))}
          <option value="__create__" disabled={creating}>
            {creating ? 'Creazione in corso...' : '+ Crea nuovo calendario "LensCRM"'}
          </option>
        </select>
        <Button size="sm" variant="ghost" icon={<RefreshCw size={13} />} onClick={fetchCalendars} loading={loading} />
      </div>

      {creating && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          Contattando Google Calendar... attendere qualche secondo
        </div>
      )}

      {error && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', margin: 0 }}>{error}</p>}

      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0, lineHeight: '1.4' }}>
        <Calendar size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        LensCRM utilizzerà <strong>esclusivamente</strong> il calendario selezionato.
        Nessun altro calendario del tuo account Google verrà letto, modificato o eliminato.
      </p>
    </div>
  );
}
